import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { fetchProposalData } from "../proposal/proposal.actions";
import { fetchBatterySizerRecommendations } from "./battery-sizer.actions";
import { BatteryRecommendation, BatterySizerResponse, BatterySizerSettings } from "./battery-sizer.types";

export interface BatterySizerState {
  loadingRecommendations: boolean;
  hasFetchedInitial: boolean;
  error: string | undefined;
  recommendations: BatteryRecommendation[];
  energyBreakdown: { consumption: number; production: number; capacity: number; hour: number }[];
  idealCapacity: number;
  settings: BatterySizerSettings;
  showSelectionPromptOverlay: boolean | null;
  // Array of indices indicating whether that legend item is toggled on
  // Needed to persist the toggled legend items when data is refetched or breakdown modal is closed
  activeLegendItems: boolean[];
}

const initialState: BatterySizerState = {
  loadingRecommendations: false,
  hasFetchedInitial: false,
  recommendations: [],
  error: undefined,
  energyBreakdown: [],
  idealCapacity: 0,
  settings: {
    defermentPercentage: 0.7,
    attachment: "backup",
    season: "summer",
    hours: {
      start: 17,
      end: 22,
    },
    features: {
      gridCharging: {
        enabled: false,
        targetStateOfCharge: 0.8,
        window: {
          start: 10,
          end: 16,
        },
      },
    },
  },
  showSelectionPromptOverlay: null,
  activeLegendItems: [],
};

const batterySizerSlice = createSlice({
  name: "batterySizer",
  initialState,
  reducers: {
    fetchBatterySizerRecommendations(state) {},
    selectBatteryRecommendation(state, action: PayloadAction<{ id: string; count: number }[]>) {},
    setHasFetchedInitial(state, action: PayloadAction<boolean>) {
      state.hasFetchedInitial = action.payload;
    },
    setError(state, action: PayloadAction<string | undefined>) {
      state.error = action.payload;
    },
    setShowSelectionPromptOverlay(state, action: PayloadAction<boolean>) {
      state.showSelectionPromptOverlay = action.payload;
    },
    toggleActiveLegendItem(state, action: PayloadAction<number>) {
      state.activeLegendItems[action.payload] = !state.activeLegendItems[action.payload];
    },
    updateRecommendation(state, action: PayloadAction<{ index: number; recommendation: BatteryRecommendation }>) {
      const { index, recommendation } = action.payload;
      const recommendedQuantity =
        state.recommendations[index].recommendedQuantity ?? state.recommendations[index].orgEquipment[0].quantity;

      state.recommendations[index] = { ...recommendation, recommendedQuantity };
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchProposalData.fulfilled, (state, action) => {
      const { batteryModel } = action.payload;
      if (batteryModel) {
        state.settings = {
          ...state.settings,
          ...batteryModel,
        };
      }
    });

    builder
      .addCase(fetchBatterySizerRecommendations.pending, (state, action) => {
        state.error = undefined;
        state.loadingRecommendations = true;
      })
      .addCase(fetchBatterySizerRecommendations.rejected, (state, action) => {
        state.error = action.payload?.message || action.error.message;
        state.loadingRecommendations = false;
      })
      .addCase(fetchBatterySizerRecommendations.fulfilled, (state, action: { payload: BatterySizerResponse }) => {
        state.recommendations = action.payload.recommendations;
        state.energyBreakdown = action.payload.daily;
        state.idealCapacity = action.payload.capacity;
        state.settings = { ...state.settings, ...action.payload.parameters };
        state.loadingRecommendations = false;
      });
  },
});

export const {
  setHasFetchedInitial,
  setError,
  setShowSelectionPromptOverlay,
  selectBatteryRecommendation,
  updateRecommendation,
  toggleActiveLegendItem,
} = batterySizerSlice.actions;

export default batterySizerSlice;
